<script setup lang="ts">
import UserAvatar from '@/core/UserAvatar.vue';
import { IGenericUser } from '@/interfaces';
import { computed, toRefs } from 'vue';
import OverlappingItemGroup from './OverlappingItemGroup.vue';
import { useDisplayName } from '@/composables/useDisplayName';

const props = withDefaults(
  defineProps<{
    users: IGenericUser[];
    /** max number of avatars to show before collapsing into a "+N" badge */
    max?: number;
    /** optional trailing label; when omitted only the avatars are shown */
    label?: string;
    /** color/emphasis utilities for the trailing label (defaults to muted secondary) */
    labelClass?: string;
    /** sm renders 16px, md 24px, and default 34px avatars */
    size?: 'default' | 'md' | 'sm';
  }>(),
  { max: 3, label: undefined, labelClass: 'text-secondary', size: 'default' },
);
const { users, max, label } = toRefs(props);
const { getUserDisplayNameOrEmail } = useDisplayName();

const extraUserNames = computed(() =>
  users.value
    .slice(max.value)
    .map((user) => getUserDisplayNameOrEmail(user))
    .join(', '),
);
</script>

<template>
  <div class="flex min-w-0 items-center gap-sm" :class="!label && 'shrink-0'">
    <OverlappingItemGroup
      :items="users"
      :max-visible="max"
      :get-key="(user, index) => user.id ?? index"
      :overlap="props.size === 'sm' ? 'xs' : 'sm'"
      class="shrink-0"
    >
      <template #item="{ item: user }">
        <UserAvatar
          :user="user"
          class="shrink-0 rounded-full ring-white"
          :class="{
            '!size-4 text-[10px] ring-1': props.size === 'sm',
            '!size-icon-md ring-2': props.size === 'md',
            '!size-icon-lg text-md ring-2': props.size === 'default',
          }"
        />
      </template>
      <template #overflow="{ count }">
        <span
          :title="extraUserNames"
          class="flex shrink-0 items-center justify-center rounded-full bg-surface-secondary font-semibold text-secondary"
          :class="{
            'h-4 min-w-4 px-xxs text-[8px] ring-1 ring-white': props.size === 'sm',
            'size-icon-md text-sm ring-2 ring-white': props.size === 'md',
            'size-icon-lg text-footer ring-2 ring-white': props.size === 'default',
          }"
        >
          +{{ count }}
        </span>
      </template>
    </OverlappingItemGroup>
    <span v-if="label" :class="['text-footer truncate font-semibold', labelClass]">{{ label }}</span>
  </div>
</template>
